import React, { useMemo } from 'react';
import Select from 'react-select';

import './TimeRangeSettings.css';
import ColorPicker from './ColorPicker';
import {
  getThemeColorKey,
  getThemeColorValue,
  normalizeThemeMode,
} from '../utils/themeColors';
import {
  TIME_RANGE_PARTS,
  createDefaultTimeRangeStream,
} from '../widgets/timeRangeParts';

const resolvePickerColor = (value, fallback = '#000000') => {
  const safeValue = typeof value === 'string' ? value.trim() : '';
  if (!safeValue) return fallback;
  if (!safeValue.startsWith('var(') || typeof window === 'undefined') {
    return safeValue;
  }

  const match = safeValue.match(/var\(\s*(--[^),\s]+)\s*(?:,\s*([^)]+))?\)/);
  if (!match) return fallback;

  const [, variableName, innerFallback] = match;
  const computed = window.getComputedStyle(document.body).getPropertyValue(variableName).trim();
  if (computed) return computed;

  if (innerFallback) {
    return resolvePickerColor(innerFallback.trim(), fallback);
  }

  return fallback;
};

const TimeRangeSettings = ({
  edit,
  setEdit,
  devices = [],
  validDatastreams = [],
  deviceOptions = [],
}) => {
  const props = edit?.props || {};
  const streams = props.streams || {};
  const hasDevices = Array.isArray(devices) && devices.length > 0;
  const themeMode = normalizeThemeMode(props.themeMode);
  const isLight = themeMode === 'light';
  const themeKey = (key) => getThemeColorKey(key, themeMode);
  const themeValue = (key, fallback) =>
    getThemeColorValue(props, key, themeMode, fallback);
  const themePickerValue = (key, fallback) =>
    resolvePickerColor(themeValue(key, fallback), resolvePickerColor(fallback, '#000000'));

  const updateProps = (patch) =>
    setEdit((prev) => ({
      ...prev,
      props: {
        ...(prev.props || {}),
        ...patch,
      },
    }));

  const updateStream = (streamKey, patch) =>
    setEdit((prev) => {
      const currentStreams = prev?.props?.streams || {};
      const currentStream = currentStreams[streamKey] || createDefaultTimeRangeStream(streamKey);

      return {
        ...prev,
        props: {
          ...(prev.props || {}),
          streams: {
            ...currentStreams,
            [streamKey]: {
              ...currentStream,
              ...patch,
            },
          },
        },
      };
    });

  const resolvedDeviceOptions = useMemo(() => {
    if (Array.isArray(deviceOptions) && deviceOptions.length > 0) {
      return deviceOptions;
    }

    return (devices || []).map((device) => ({
      value: Number(device?.id) || 0,
      label: device?.name || `Device ${device?.id ?? ''}`,
    }));
  }, [deviceOptions, devices]);

  const getDatastreamList = (stream) => {
    if (!hasDevices) return (validDatastreams || []).filter(Boolean);
    const streamDevice = (devices || []).find(
      (device) => Number(device?.id) === Number(stream?.deviceId)
    );
    return (streamDevice?.template?.datastreams || []).filter(Boolean);
  };

  const getDatastreamOptions = (stream) =>
    getDatastreamList(stream).map((datastream) => ({
      value: Number(datastream?.id),
      label: `${datastream?.name || 'Datastream'} [${datastream?.pin || '-'}]`,
    }));

  return (
    <div className="trs-settings">
      <div className="trs-partGrid">
        {TIME_RANGE_PARTS.map((part) => {
          const stream = streams[part.key] || createDefaultTimeRangeStream(part.key);
          const isEnabled = typeof stream?.enabled === 'boolean' ? stream.enabled : true;
          const datastreamOptions = getDatastreamOptions(stream);

          return (
            <div
              key={part.key}
              className={`trs-partCard ${isEnabled ? 'is-enabled' : 'is-disabled'}`}
            >
              <div className="trs-partHeader">
                <div className="trs-partHeading">
                  <span className="trs-partTitle">{part.shortLabel}</span>
                  <span className="trs-partCaption">{part.settingsLabel}</span>
                </div>

                <button
                  type="button"
                  className={`trs-toggle ${isEnabled ? 'is-on' : ''}`}
                  aria-pressed={isEnabled}
                  aria-label={`${isEnabled ? 'Disable' : 'Enable'} ${part.label}`}
                  onClick={() => updateStream(part.key, { enabled: !isEnabled })}
                >
                  <span className="trs-toggleThumb" />
                </button>
              </div>

              <div className="trs-partBody">
                {hasDevices ? (
                  <div className="form-item trs-formItem">
                    <label className="form-lbl trs-formLabel">Device</label>
                    <Select
                      classNamePrefix="react-select"
                      options={resolvedDeviceOptions}
                      value={
                        resolvedDeviceOptions.find(
                          (option) => Number(option.value) === Number(stream?.deviceId)
                        ) || null
                      }
                      onChange={(selectedOption) =>
                        updateStream(part.key, {
                          deviceId: selectedOption ? Number(selectedOption.value) : 0,
                          dataId: null,
                          pin: '',
                          value: stream?.value ?? createDefaultTimeRangeStream(part.key).value,
                        })
                      }
                      isClearable={true}
                    />
                  </div>
                ) : null}

                <div className="form-item trs-formItem">
                  <label className="form-lbl trs-formLabel">Datastream</label>
                  <Select
                    classNamePrefix="react-select"
                    options={datastreamOptions}
                    value={
                      datastreamOptions.find(
                        (option) => Number(option.value) === Number(stream?.dataId)
                      ) || null
                    }
                    onChange={(selectedOption) => {
                      const selectedDatastream = getDatastreamList(stream).find(
                        (datastream) => Number(datastream?.id) === Number(selectedOption?.value)
                      );

                      updateStream(part.key, {
                        dataId: selectedOption ? Number(selectedOption.value) : null,
                        pin: selectedDatastream?.pin || '',
                        value:
                          selectedDatastream?.defaultValue !== undefined
                            ? selectedDatastream.defaultValue
                            : stream?.value ?? createDefaultTimeRangeStream(part.key).value,
                      });
                    }}
                    isClearable={true}
                    isDisabled={hasDevices && !stream?.deviceId}
                  />
                </div>

                <div className="form-item trs-formItem">
                  <label className="form-lbl trs-formLabel">Default Value</label>
                  <input
                    type="number"
                    className="add-column"
                    min={part.fallbackMin}
                    max={part.fallbackMax}
                    step={1}
                    value={stream?.value ?? ''}
                    onChange={(e) =>
                      updateStream(part.key, {
                        value: e.target.value === '' ? '' : Number(e.target.value),
                      })
                    }
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="trs-sizeGrid">
        <div className="form-item trs-formItem">
          <label className="form-lbl trs-formLabel">Title Size</label>
          <input
            type="number"
            className="add-column"
            min={12}
            max={48}
            value={Number(props.titleSize ?? 20)}
            onChange={(e) => updateProps({ titleSize: Number(e.target.value || 20) })}
          />
        </div>

        <div className="form-item trs-formItem">
          <label className="form-lbl trs-formLabel">Value Size</label>
          <input
            type="number"
            className="add-column"
            min={24}
            max={120}
            value={Number(props.valueSize ?? 64)}
            onChange={(e) => updateProps({ valueSize: Number(e.target.value || 64) })}
          />
        </div>

        <div className="form-item trs-formItem">
          <label className="form-lbl trs-formLabel">Border Radius</label>
          <input
            type="number"
            className="add-column"
            min={0}
            max={48}
            value={Number(props.borderRadius ?? 20)}
            onChange={(e) => updateProps({ borderRadius: Number(e.target.value || 0) })}
          />
        </div>
      </div>

      <div className="trs-optionRow">
        <label className="trs-optionPill">
          <span className="trs-optionLabel">Pad Hours</span>
          <input
            className="checkbox-input"
            type="checkbox"
            checked={props.showLeadingZeroHours ?? true}
            onChange={(e) => updateProps({ showLeadingZeroHours: e.target.checked })}
          />
        </label>

        <label className="trs-optionPill">
          <span className="trs-optionLabel">Pad Minutes</span>
          <input
            className="checkbox-input"
            type="checkbox"
            checked={props.showLeadingZeroMinutes ?? true}
            onChange={(e) => updateProps({ showLeadingZeroMinutes: e.target.checked })}
          />
        </label>
      </div>

      <div className="trs-colorGrid">
        {[
          ['cardColor', 'Card Color', 'var(--time-range-card-bg)'],
          ['borderColor', 'Border Color', 'var(--time-range-border-color)'],
          ['titleColor', `Title Color (${isLight ? 'Light' : 'Dark'})`, 'var(--time-range-title-color)'],
          ['valueColor', `Value Color (${isLight ? 'Light' : 'Dark'})`, 'var(--time-range-value-color)'],
          ['shadowColor', `Shadow Color (${isLight ? 'Light' : 'Dark'})`, 'var(--time-range-shadow-color)'],
        ].map(([key, label, fallback]) => (
          <div className="form-item trs-formItem" key={key}>
            <label className="form-lbl trs-formLabel">{label}</label>
            <ColorPicker
              id={`time-range-${key}`}
              color={themePickerValue(key, fallback)}
              setColor={(color) => updateProps({ [themeKey(key)]: color })}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default TimeRangeSettings;
